import {defineStore} from 'pinia';
import {hexToRgbString, isCustomColor, isDarkColor} from "../../helpers/common";
import phoneDialCodesData from "../../helpers/phoneDialCodes";

export const usePortalContentAndDesignStore = defineStore('portalContentAndDesignStore', {
    state: () => ({
        // Shared
        portalInitialized: false,
        dashboardDataLoaded: false,
        initialPortalPageData: null,
        liveDesignerEnabled: false,
        liveDesignerDirty: false, // Track if user has made changes
        program: {},
        portalLayoutSettings: {},
        designTemplates: [],
        appliedDesignTemplateId: null,
        translationSettings: {},
        translationSettingsConfig: [],
        availableTranslations: [],
        selectedPredefinedLanguage: '',
        selectedLanguage: null,
        defaultLanguage: 'en',
        multilanguageEnabled: false,
        partneroBrandingSettings: {},
        srcRoutes: {},
        turnstileSettings: false,
        // Spatie honeypot: field names + encrypted timestamp injected from the backend.
        honeypotSettings: null,
        // Reactive values for the hidden honeypot inputs. `name` must stay empty for
        // real users; a filled value indicates a spam bot. `validFrom` is prefilled.
        honeypotFormData: {
            name: '',
            validFrom: '',
        },
        countryOptions: [],
        phoneDialCodes: [],
        isPreview: false,

        // Partner tags for resource visibility filtering
        availablePartnerTags: [],

        // Auth-specific
        isAuthView: false,
        authUser: {},
        isSubAccount: false,
        subAccountsEnabled: false,
        subAccountShouldChangePassword: false,
        subAccountPasswordValidationRules: {},
        avatarDetails: null,
        showOnboarding: false,
        onboardingStep: null,
        userMenu: {},
        menu: {},
        dashboardPageSettings: {
            refer_a_friend_portal_page_title: '',
            refer_a_friend_portal_page_description: '',
            refer_a_friend_portal_dashboard_branding: 'symbol',
            dashboard_resources: [],
        },
        referralsPageSettings: {},
        referralPageSettings: {},
        resourcesPageSettings: {},
        // Tracks whether the resources page settings have been loaded from the
        // backend. The Live Designer must not persist resources until this is true,
        // otherwise it would overwrite stored resources with an empty array.
        resourcesPageSettingsLoaded: false,
        payoutsPageSettings: {},
        reportsPageSettings: {},
        profilePageSettings: {},
        invitationPageSettings: {},
        approvalPageSettings: {},
        messagesPageSettings: {},
        messagesPageSettingsLoaded: false,
        invitationKey: null,
        appDiskUrl: null,

        // Public-only
        signup: false,
        loginPageSettings: {},
        landingPageSettings: {},
        landingPageResources: {},
        leadsPageSettings: {},
        signupPageSettings: {},
        resetPasswordPageSettings: {},

        // Forms
        signupFormData: {
            name: '',
            last_name: '',
            username: '',
            email: '',
            password: '',
            tos: false,
            privacy_policy: false,
            marketing_consent: false,
            key: '',
            cfields: {},
            partner_tags: [],
        },
        signupFormErrors: {
            responseError: null,
            validationErrors: null,
        },
        leadsFormData: {
            name: '',
            username: '',
            submitter_name: null,
            submitter_email: null,
            cfields: {},
        },
        leadsFormErrors: {
            responseError: null,
            validationErrors: null,
        },
        leadsPartnerLoginFormData: {
            email: null,
            password: null,
            otp_token: null,
            otp_identifier: null,
            otp: {
                step: 1
            }
        },
        approvalFormData: {
            cfields: {},
        },
        approvalFormErrors: {
            responseError: null,
            validationErrors: null,
        },
        
        // Inline approval functionality
        showInlineApproval: false,
        approvalData: {},
        loginFormData: {
            email: '',
            password: '',
            otpIdentifier: '',
            otpIniSuccessMessage: '',
            token: '',
        },
        loginEmailLocked: false,
        loginAutoSubmit: false,
        loginFormErrors: {
            responseError: null,
            validationErrors: null
        },
        loggingInProgress: false
    }),

    actions: {
        initializePortalContent(data, isAuthView = false, isPreview = false) {
            this.isAuthView = isAuthView;
            this.isPreview = isPreview;
            this.portalInitialized = true;
            this.initialPortalPageData = JSON.parse(JSON.stringify(data));
            this.liveDesignerEnabled = data.live_designer_enabled || false;
            this.program = data.program || {};
            this.portalLayoutSettings = data.portal_layout_settings || {};
            this.designTemplates = data.design_templates || [];
            this.appliedDesignTemplateId = null;
            this.translationSettings = data.translation_settings || {};
            this.translationSettingsConfig = data.translation_settings_config || {};
            this.availableTranslations = data.available_translations || [];
            this.defaultLanguage = data.default_language || 'en';
            this.multilanguageEnabled = data.multilanguage_enabled || false;
            
            // Check localStorage first for persisted language preference
            const storageKey = this.program?.id ? `portal_language_${this.program.id}` : 'portal_language';
            const storedLanguage = typeof localStorage !== 'undefined' ? localStorage.getItem(storageKey) : null;
            
            // If multilanguage is disabled, clear language preferences
            if (!this.multilanguageEnabled) {
                this.selectedLanguage = null;
                // Clear localStorage if multilanguage is disabled
                if (typeof localStorage !== 'undefined' && storedLanguage) {
                    localStorage.removeItem(storageKey);
                }
            } else {
                // Use localStorage value if available, otherwise use server value
                if (storedLanguage) {
                    this.selectedLanguage = storedLanguage;
                    // If localStorage differs from server, sync and reload to apply translations
                    if (data.selected_language !== storedLanguage) {
                        this.syncLanguageToSession(storedLanguage).then(() => {
                            // Reload page to apply translations
                            window.location.reload();
                        });
                    }
                } else {
                    this.selectedLanguage = data.selected_language || null; // null means use default/admin translations
                    // If server has a language but localStorage doesn't, sync localStorage
                    if (data.selected_language) {
                        if (typeof localStorage !== 'undefined') {
                            localStorage.setItem(storageKey, data.selected_language);
                        }
                    }
                }
            }
            this.partneroBrandingSettings = data.partnero_branding_settings || {};
            this.srcRoutes = data.src_routes || {};
            this.countryOptions = data.country_options || [];
            this.phoneDialCodes = data.phone_dial_codes || [];
            this.turnstileSettings = data.turnstile_settings || null;
            this.honeypotSettings = data.honeypot || null;
            this.honeypotFormData = {
                name: '',
                validFrom: data.honeypot?.encryptedValidFrom || '',
            };
            this.appDiskUrl = data.app_disk_url || null;
            this.availablePartnerTags = data.available_partner_tags || [];

            if (isAuthView) {
                this.authUser = data.auth_user || {};
                this.isSubAccount = data.is_sub_account || false;
                this.subAccountsEnabled = data.sub_accounts_enabled || false;
                this.subAccountShouldChangePassword = data.sub_account_should_change_password || false;
                this.subAccountPasswordValidationRules = data.sub_account_password_validation_rules || {};
                this.avatarDetails = data.avatar_details || null;
                this.showOnboarding = data.show_onboarding || false;
                this.onboardingStep = data.onboarding_step || null;
                this.userMenu = data.user_menu || {};
                this.menu = data.menu || {};
                this.invitationKey = data.invitation_key || null;

                if (data.invitation_page_settings && Object.keys(data.invitation_page_settings).length > 0) {
                    this.invitationPageSettings = data.invitation_page_settings;
                }
                
                // Load dashboard page settings if provided server-side
                if (data.dashboardPageSettings) {
                    this.dashboardPageSettings = data.dashboardPageSettings;
                    this.dashboardDataLoaded = true;
                }
            } else {
                this.subAccountsEnabled = data.sub_accounts_enabled || false;
                this.leadsPageSettings = data.leads_page_settings || {};
                this.loginPageSettings = data.login_page_settings || {};
                this.landingPageSettings = data.landing_page_settings || {};
                this.landingPageResources = data.landingPageResources || data.landing_page_settings?.landing_page_resources || {};
                // Ensure resources array exists and is reactive
                if (!this.landingPageResources.resources) {
                    this.landingPageResources.resources = [];
                }
                // Ensure general object exists with default values for program details
                this.landingPageResources.general = {
                    introduction: '',
                    description: '',
                    commission_text: '',
                    program_branding_type: 'symbol',
                    display_signup_and_login_links: false,
                    display_submit_lead_link: false,
                    display_commission: true,
                    show_footer: true,
                    ...this.landingPageResources.general
                };
                // Sync landingPageSettings.landing_page_resources with landingPageResources
                // so portal components and Live Designer edit the same object
                if (this.landingPageSettings) {
                    this.landingPageSettings.landing_page_resources = this.landingPageResources;
                }
                this.signupPageSettings = data.signup_page_settings || {};
                this.resetPasswordPageSettings = data.reset_password_page_settings || {};
                this.populateSignupFormCustomFields();
                this.populateLeadsFormCustomFields();
            }
        },

        initializePortalPageContent(page, id = null) {
            let url;
            if (page === 'referral' && id) {
                url = route('partners_int.page_settings.referral', {id});
            } else {
                url = route(`partners_int.page_settings.${page}`);
            }

            return axios.get(url)
                .then(response => {
                    if (page === 'dashboard') {
                        // Replace with backend data (this is the correct behavior for initial load)
                        this.dashboardPageSettings = response.data || {};
                        window.growthChart = response.data.growth_chart || null;
                        // Mark portal as initialized for dashboard page
                        this.portalInitialized = true;
                        this.dashboardDataLoaded = true;
                    }
                    if (page === 'referrals') {
                        this.referralsPageSettings = response.data || {};
                    }
                    if (page === 'referral') {
                        this.referralPageSettings = response.data || {};
                    }
                    if (page === 'leads') {
                        this.leadsPageSettings = response.data || {};
                    }
                    if (page === 'resources') {
                        this.resourcesPageSettings = response.data || {};
                        this.resourcesPageSettingsLoaded = true;
                    }
                    if (page === 'payouts') {
                        this.payoutsPageSettings = response.data || {};
                    }
                    if (page === 'reports') {
                        this.reportsPageSettings = response.data || {};
                    }
                    if (page === 'affiliate-profile' || page === 'profile') {
                        this.profilePageSettings = response.data || {};
                    }
                    if (page === 'messages') {
                        this.messagesPageSettings = response.data || {};
                        this.messagesPageSettingsLoaded = true;
                    }
                    if (page === 'invitation') {
                        this.invitationPageSettings = response.data || {};
                    }
                    if (page === 'approval') {
                        this.approvalPageSettings = response.data || {};
                        this.populateApprovalFormCustomFields()
                    }
                })
                .catch(error => {
                    console.error('Error initializing portal content:', error);
                });
        },

        resetCurrentPageToInitialState() {
            if (this.initialPortalPageData) {
                this.initializePortalContent(this.initialPortalPageData, this.isAuthView);
                this.liveDesignerDirty = false;
            }
        },
        
        markAsDirty() {
            if (this.liveDesignerEnabled) {
                this.liveDesignerDirty = true;
            }
        },

        applyDesignTemplate(template) {
            if (!template || !template.settings) {
                return;
            }
            // Merge the theme's style settings into the current layout settings.
            // Reassigning the object triggers the deep watcher (marks the designer dirty).
            this.portalLayoutSettings = {
                ...this.portalLayoutSettings,
                ...template.settings,
            };
            this.appliedDesignTemplateId = template.id;
            this.markAsDirty();
        },

        // Clear the "Applied" theme badge as soon as the current settings diverge from
        // the applied theme (i.e. the user manually changed something in Design settings).
        // Compares only the keys the theme defines, so harmless no-op writes (e.g. color
        // pickers re-emitting their mounted value when switching tabs) don't clear it.
        syncAppliedDesignTemplate() {
            if (!this.appliedDesignTemplateId) {
                return;
            }
            const template = (this.designTemplates || []).find(t => t.id === this.appliedDesignTemplateId);
            if (!template || !template.settings) {
                this.appliedDesignTemplateId = null;
                return;
            }
            const stillMatches = Object.entries(template.settings).every(([key, value]) => {
                return String(this.portalLayoutSettings?.[key] ?? '') === String(value ?? '');
            });
            if (!stillMatches) {
                this.appliedDesignTemplateId = null;
            }
        },
        
        markAsClean() {
            this.liveDesignerDirty = false;
        },

        getPhoneFieldDefaultDialCode(field) {
            const allCodes = this.phoneDialCodes?.length ? this.phoneDialCodes : phoneDialCodesData;
            let available = allCodes;
            if (field.code_selection === 'selected' && field.selected_codes?.length) {
                available = allCodes.filter(dc => field.selected_codes.includes(dc.alpha2));
            }
            if (available.length === 1) return available[0].dial_code;
            if (field.default_dial_code) return field.default_dial_code;
            try {
                const locale = navigator.language || '';
                const cc = locale.split('-')[1]?.toUpperCase();
                if (cc) {
                    const match = phoneDialCodesData.find(dc => dc.alpha2 === cc);
                    if (match) return match.dial_code;
                }
            } catch {}
            return '+1';
        },

        populateSignupFormCustomFields() {
            const signupCustomFields = this.signupPageSettings?.signup_form?.fields || [];
            signupCustomFields.forEach((field) => {
                if (field?.id) {
                    if (field.type === 'checkbox') {
                        this.signupFormData.cfields[field.id] = [];
                    } else if (field.type === 'phone') {
                        this.signupFormData.cfields[field.id] = { dial_code: this.getPhoneFieldDefaultDialCode(field), number: '' };
                    } else {
                        this.signupFormData.cfields[field.id] = '';
                    }
                }
            });
        },

        populateLeadsFormCustomFields() {
            const leadsCustomFields = this.leadsPageSettings?.leads_form?.fields || [];
            leadsCustomFields.forEach((field) => {
                if (field?.id) {
                    if (field.type === 'checkbox') {
                        this.leadsFormData.cfields[field.id] = [];
                    } else if (field.type === 'phone') {
                        this.leadsFormData.cfields[field.id] = { dial_code: this.getPhoneFieldDefaultDialCode(field), number: '' };
                    } else {
                        this.leadsFormData.cfields[field.id] = '';
                    }
                }
            });
        },

        populateApprovalFormCustomFields() {
            const approvalCustomFields = this.approvalPageSettings?.approval_form?.fields || [];
            approvalCustomFields.forEach((field) => {
                if (field?.id) {
                    if (field.type === 'checkbox') {
                        this.approvalFormData.cfields[field.id] = [];
                    } else if (field.type === 'phone') {
                        this.approvalFormData.cfields[field.id] = { dial_code: this.getPhoneFieldDefaultDialCode(field), number: '' };
                    } else {
                        this.approvalFormData.cfields[field.id] = '';
                    }
                }
            });
        },

        resetFormErrors(formName) {
            if (formName === 'signup') {
                this.signupFormErrors.responseError = null;
                this.signupFormErrors.validationErrors = null;
            } else if (formName === 'leads') {
                this.leadsFormErrors.responseError = null;
                this.leadsFormErrors.validationErrors = null;
            } else if (formName === 'login') {
                this.loginFormErrors.responseError = null;
                this.loginFormErrors.validationErrors = null;
            } else if (formName === 'approval') {
                this.approvalFormErrors.responseError = null;
                this.approvalFormErrors.validationErrors = null;
            }
        },

        resetLeadsFormAndErrorData() {
            this.leadsFormData.name = '';
            this.leadsFormData.username = '';
            this.resetFormErrors('leads');
            this.populateLeadsFormCustomFields();
        },

        getAppFileSrc(path, defaultValue = null) {
            if (path && this.appDiskUrl) {
                return this.appDiskUrl.replace(/\/+$/, '') + '/' + path.replace(/^\/+/, '');
            }
            return defaultValue;
        },
        
        setSelectedLanguage(language) {
            this.selectedLanguage = language;
        },
        
        async syncLanguageToSession(language) {
            // Silently sync localStorage language to session without reloading
            try {
                await fetch(this.getLanguageStoreRoute, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
                    },
                    body: JSON.stringify({ language: language })
                });
            } catch (error) {
                // Silent fail - don't interrupt user experience
                console.debug('Failed to sync language to session:', error);
            }
        },
        
        async loadSelectedLanguage() {
            try {
                const response = await fetch(this.getLanguageShowRoute);
                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.language) {
                        this.selectedLanguage = data.language;
                        // Sync to localStorage
                        const storageKey = this.program?.id ? `portal_language_${this.program.id}` : 'portal_language';
                        if (typeof localStorage !== 'undefined') {
                            localStorage.setItem(storageKey, data.language);
                        }
                    }
                }
            } catch (error) {
                console.error('Failed to load selected language:', error);
            }
        }
    },

    getters: {
        // Spatie honeypot: extra fields to merge into axios form payloads.
        // Returns an empty object when honeypot is disabled/absent.
        getHoneypotPayload(state) {
            const hp = state.honeypotSettings;
            if (!hp || !hp.enabled || !hp.nameFieldName) {
                return {};
            }
            return {
                [hp.nameFieldName]: state.honeypotFormData.name ?? '',
                [hp.validFromFieldName]: state.honeypotFormData.validFrom ?? '',
            };
        },

        // Routes
        getFileUploadRoute(state) {
            const routeName = state.liveDesignerEnabled
                ? 'partners_int.live-designer.file.upload'
                : 'app_int.program_settings_file.upload';
            return route(routeName, {program: state.program.id});
        },
        getFileDeleteRoute(state) {
            const routeName = state.liveDesignerEnabled
                ? 'partners_int.live-designer.file.delete'
                : 'app_int.program_settings_file.delete';
            return route(routeName, {program: state.program.id});
        },
        getFileUploadNoImageRoute() {
            return "/images/upload-image.png"
        },
        getFileResourcesUploadRoute(state) {
            return route('partners_int.live-designer.resources.file.upload', {program: state.program.id});
        },
        getFileResourcesDeleteRoute(state) {
            return route('partners_int.live-designer.resources.file.delete', {program: state.program.id});
        },
        getDashboardFileUploadRoute(state) {
            return route('app_int.program.portal.dashboard.resources.file_upload', {program: state.program.id})
        },
        getDashboardFileDeleteRoute(state) {
            return route('app_int.program.portal.dashboard.resources.file_delete', {program: state.program.id})
        },
        getResourcePageFileUploadRoute(state) {
            return route('app_int.program.portal.resources.file_upload', {program: state.program.id})
        },
        getResourcePageFileDeleteRoute(state) {
            return route('app_int.program.portal.resources.file_delete', {program: state.program.id})
        },
        getResourcePageGenerateExampleRoute(state) {
            return state.liveDesignerEnabled
                ? route('partners_int.live-designer.resources.generate-example')
                : route('app_int.program.portal.resources.generate_example', {program: state.program.id})
        },
        getLeadsRoute() {
            return route('partners.leads.show');
        },
        getLoginRoute() {
            return route('partners.login.show');
        },
        getRegisterRoute() {
            return route('partners.register.show');
        },
        getForgotPasswordRoute() {
            return route('partners.password.request.show');
        },
        getSocialAuthLoginRedirectRoute() {
            return (provider) => route('partners.affiliate.social-auth.user.login.redirect', {provider: provider});
        },
        getSocialAuthRegisterRedirectRoute() {
            return (provider) => route('partners.affiliate.social-auth.user.register.redirect', {provider: provider});
        },
        getHomeRoute() {
            return route('partners.dashboard');
        },
        getPartnerLogoutRoute() {
            return route('partners.logout.show');
        },
        getSubAccountLoginRoute() {
            return route('partners.sub_account.login.show');
        },
        getSubAccountLogoutRoute() {
            return route('partners.sub_account.logout.show');
        },
        getPayoutRequestRoute() {
            return route('partners_int.payouts.request');
        },
        getPayoutAvailableCommissionsRoute() {
            return route('partners_int.payouts.available-commissions');
        },
        getPayoutItemsRoute: () => (payoutId) => route('partners_int.payouts.items', {payout: payoutId}),
        getPayoutSetPayoutInfoRoute(state) {
            return route('partners_int.payouts.save-payout-details', {program: state.program.id});
        },
        getRefreshBankRequirementsRoute() {
            return route('partners_int.payouts.bank_transfer.refresh-requirements');
        },
        getPayoutCancelRoute() {
            return route('partners_int.payouts.cancel')
        },
        getPayoutResubmitRoute() {
            return route('partners_int.payouts.resubmit')
        },
        getReportingDataRoute() {
            return route('partners_int.views.reporting.referrals_analytics')
        },
        getProfileAvatarUploadRoute() {
            return route('partners_int.profile.avatar.upload')
        },
        getProfileAvatarDeleteRoute() {
            return route('partners_int.profile.avatar.delete')
        },
        getDeleteAccountRoute() {
            return route('partners_int.delete_account')
        },
        
        // Translation helpers
        getTranslation(settingKey, fallback = '') {
            const translation = this.translationSettings[settingKey];
            if (translation) {
                return translation;
            }
            return fallback;
        },
        getLanguageStoreRoute() {
            return route('partners.language.store');
        },
        getLanguageShowRoute() {
            return route('partners.language.show');
        },


        // Page Settings
        getLandingPageSettings(state) {
            return state.landingPageSettings;
        },
        
        // Program commission description for landing page (fallback when commission_text is empty)
        programCommissionDescription(state) {
            const commissionStructure = state.initialPortalPageData?.program_commission_structure;
            if (!commissionStructure || Array.isArray(commissionStructure)) {
                return '';
            }
            
            // Use the default commission display value
            if (commissionStructure.default_commission?.display) {
                return commissionStructure.default_commission.display;
            }
            
            // Fallback to value if display is not available
            if (commissionStructure.default_commission?.value) {
                return commissionStructure.default_commission.value;
            }
            
            return '';
        },


        // Other
        getCountryOptions: (state) => state.countryOptions,
        getPhoneDialCodes: (state) => state.phoneDialCodes,

        // Public pages Colors & Styling
        getLinkColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.public_page_content_link_color)
                ? 'text-custom-link'
                : `text-${state.portalLayoutSettings.public_page_content_link_color}`;
        },
        getPageBackgroundColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.public_page_background_color)
                ? 'bg-custom-bg'
                : `bg-${state.portalLayoutSettings.public_page_background_color}`;
        },
        getPageBackgroundGradientColorClass(state) {
            if (state.portalLayoutSettings.public_page_background_gradient_color === 'none') {
                return '';
            }
            return `bg-gradient-to-b from-${
                isCustomColor(state.portalLayoutSettings.public_page_background_gradient_color)
                    ? "custom-gradient-bg"
                    : state.portalLayoutSettings.public_page_background_gradient_color
            }`
        },
        getPageContentBackgroundColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.public_page_content_background_color)
                ? 'bg-content-bg'
                : `bg-${state.portalLayoutSettings.public_page_content_background_color}`;
        },
        getPageContentButtonColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.public_page_content_button_color)
                ? 'custom-content'
                : state.portalLayoutSettings.public_page_content_button_color;
        },
        getPageContentButtonTextColorClass() {
            return 'portal-btn-text';
        },
        getPageContentLayoutCornerClass(state) {
            return state.portalLayoutSettings.referral_portal_content_layout_corners || 'rounded-xl';
        },
        getPublicPageButtonCorners(state) {
            // Check if it's a referral portal
            const programType = state.program?.program_type?.type || state.program?.programType?.type;
            
            if (programType === 'refer_a_friend') {
                // Referral portal styling
                const corners = state.portalLayoutSettings?.referral_portal_content_layout_corners || 'rounded-xl';
                return corners === 'none' ? 'rounded-none' : corners;
            } else {
                // Affiliate portal styling
                const corners = state.portalLayoutSettings?.public_page_content_layout_corners || 'rounded-xl';
                return corners === 'none' ? 'rounded-none' : corners;
            }
        },
        getInputStyle(state) {
            const defaultStyle = 'transition-shadow block w-full rounded focus:ring-0 py-2.5 px-3.5 text-sm border-gray-300 focus:border-gray-400';

            if (state.portalLayoutSettings.public_page_content_input_style === 'white') {
                return [defaultStyle, 'bg-white text-gray-900'].join(' ');
            } else {
                return [defaultStyle, `bg-transparent ${this.getPageContentTextColorClass}`].join(' ');
            }
        },
        getCheckboxInputStyle(state) {
            const defaultStyle = 'h-5 w-5 rounded cursor-pointer focus:ring-0 focus:ring-offset-0 border-gray-300 focus:border-gray-400';
            if (state.portalLayoutSettings.public_page_content_input_style === 'white') {
                return [defaultStyle, 'bg-white text-gray-900'].join(' ');
            } else {
                return [defaultStyle, `bg-transparent ${this.getPageContentTextColorClass}`].join(' ');
            }
        },
        getPageContainerClasses(state) {
            const layout = state.portalLayoutSettings.public_page_content_layout;
            const sidebar = state.portalLayoutSettings.portal_signup_sidebar_side;
            const sidebarWidth = state.portalLayoutSettings.portal_signup_sidebar_width;
            const image = state.srcRoutes.public_page_background_image;
            const corners = state.portalLayoutSettings.public_page_content_layout_corners;
            const style = state.portalLayoutSettings.public_page_content_layout_style;

            const classes = [];

            if (layout === "one") {
                if (image && sidebar === "none") classes.push("px-10 py-10");
            } else if (["two", "three"].includes(layout)) {
                classes.push(isCustomColor(state.portalLayoutSettings.public_page_content_background_color)
                    ? "bg-content-bg"
                    : `bg-${state.portalLayoutSettings.public_page_content_background_color}`);
                if (layout === "two") {
                    if (style === "shadow") classes.push("shadow-[0_0_16px_rgba(0,0,0,0.08)]");
                    else if (style === "border") classes.push("border");
                    if (corners !== "none") classes.push(corners);
                }
            }

            if (layout !== "one") {
                if (sidebarWidth === "lg:w-8/12") classes.push("px-12 py-12");
                else if (sidebarWidth === "lg:w-6/12") classes.push("px-10 py-10");
                else classes.push("px-8 py-8");
            }

            return classes.join(" ");
        },

        getPortalLayoutStyleClasses(state) {
            const style = state.portalLayoutSettings.portal_layout_layout_style;
            if (style === 'shadow') return 'shadow-[0_0_16px_rgba(0,0,0,0.08)]';
            if (style === 'border') return 'border border-gray-200 border-opacity-75';
            return '';
        },

        // Auth-pages colors & styling
        getPortalLayoutBackgroundColor(state) {
            return isCustomColor(state.portalLayoutSettings.portal_layout_background_color)
                ? 'bg-custom-bg'
                : `bg-${state.portalLayoutSettings.portal_layout_background_color}`;
        },
        // True when the authenticated dashboard background is a dark custom color.
        // Used to flip subtle black overlays / light inputs to dark-mode-friendly variants.
        getPortalLayoutIsDarkBackground(state) {
            return isDarkColor(state.portalLayoutSettings.portal_layout_background_color);
        },
        // An "elevated surface" derived from the dark theme background, used by floating
        // panels (dropdowns, date picker, multiselect) so they match the active theme
        // (near-black for Onyx, navy for Midnight) instead of a hardcoded slate.
        // Returns null when the background is not a dark custom color.
        getPortalLayoutElevatedSurface(state) {
            const background = state.portalLayoutSettings.portal_layout_background_color;
            if (!isDarkColor(background)) {
                return null;
            }
            const channels = hexToRgbString(background).split(',').map(v => parseInt(v.trim(), 10));
            const elevate = (c) => Math.min(255, (isNaN(c) ? 17 : c) + 18);
            return `rgb(${elevate(channels[0])}, ${elevate(channels[1])}, ${elevate(channels[2])})`;
        },
        getNavBackgroundColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.portal_layout_brand_primary_color)
                ? 'bg-custom-primary'
                : `bg-${state.portalLayoutSettings.portal_layout_brand_primary_color}`;
        },
        getBorderNavigationColorClass(state) {
            if (state.portalLayoutSettings.portal_layout_navigation_color === 'white') {
                return 'border-b border-white border-opacity-10'
            } else if (state.portalLayoutSettings.portal_layout_navigation_color === 'black') {
                return 'border-b border-black border-opacity-10'
            } else if (state.portalLayoutSettings.portal_layout_navigation_color === 'gray') {
                return 'border-b border-gray-200 border-gray-75'
            } else {
                return 'border-b border-white border-opacity-10'
            }
        },
        getTextColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.portal_layout_text_color)
                ? 'text-custom-text'
                : `text-${state.portalLayoutSettings.portal_layout_text_color}`;
        },
        // Partner coupons with `details` built from translated duration words, e.g. "15%", "15% once", "15% for 3 months"
        partnerPromotionCodes(state) {
            const t = state.translationSettings;
            const units = {
                hours: ['portal_translation_pages_coupon_hour', 'portal_translation_pages_coupon_hours', 'hour', 'hours'],
                days: ['portal_translation_pages_coupon_day', 'portal_translation_pages_coupon_days', 'day', 'days'],
                months: ['portal_translation_pages_coupon_month', 'portal_translation_pages_coupon_months', 'month', 'months'],
                years: ['portal_translation_pages_coupon_year', 'portal_translation_pages_coupon_years', 'year', 'years'],
            };

            const duration = ({duration_type: type, duration_value: value}) => {
                if (type === 'once') {
                    return t.portal_translation_pages_coupon_once || 'once';
                }
                const unit = units[type];
                if (!unit) {
                    return '';
                }
                const unitText = value === 1 ? (t[unit[0]] || unit[2]) : (t[unit[1]] || unit[3]);
                return `${t.portal_translation_pages_coupon_for || 'for'} ${value} ${unitText}`;
            };

            return (state.resourcesPageSettings?.partner_promotion_codes || []).map(promotionCode => ({
                ...promotionCode,
                details: [promotionCode.discount.amount, duration(promotionCode.discount)].filter(Boolean).join(' ')
                    + promotionCode.discount.restrictions,
            }));
        },
        getTextPrimaryColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.portal_layout_brand_primary_color)
                ? 'text-custom-primary'
                : `text-${state.portalLayoutSettings.portal_layout_brand_primary_color}`;
        },
        getBackgroundPrimaryColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.portal_layout_brand_primary_color)
                ? 'bg-custom-primary'
                : `bg-${state.portalLayoutSettings.portal_layout_brand_primary_color}`;
        },
        getBorderPrimaryColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.portal_layout_brand_primary_color)
                ? 'border-custom-primary'
                : `border-${state.portalLayoutSettings.portal_layout_brand_primary_color}`;
        },
        getBorderTextColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.portal_layout_text_color)
                ? 'border-custom-text'
                : `border-${state.portalLayoutSettings.portal_layout_text_color}`;
        },
        getPrimaryColorRaw(state) {
            return isCustomColor(state.portalLayoutSettings.portal_layout_brand_primary_color)
                ? 'custom-primary'
                : state.portalLayoutSettings.portal_layout_brand_primary_color;
        },
        getPortalLayoutButtonColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.portal_layout_button_color)
                ? 'custom'
                : state.portalLayoutSettings.portal_layout_button_color || 'teal';
        },
        getPortalLayoutCornersClass(state) {
            let corners = state.portalLayoutSettings.portal_layout_layout_corners || 'rounded-xl';
            // Map legacy 'rounded' value to 'rounded-lg' for backwards compatibility
            if (corners === 'rounded') {
                corners = 'rounded-lg';
            }
            return corners === 'none' ? 'rounded-none' : corners;
        },
        getLoginRoute(state) {
            return state.srcRoutes?.login || '/login';
        },
        getRegisterRoute(state) {
            return state.srcRoutes?.register || '/register';
        },
        isExternalRegisterRoute(state) {
            const url = state.srcRoutes?.register;
            return !!url && url.startsWith('http');
        },
        hasUnsavedChanges(state) {
            return state.liveDesignerEnabled && state.liveDesignerDirty;
        },
        getInputStyle(state) {
            // Check if it's a referral portal
            const programType = state.program?.program_type?.type || state.program?.programType?.type;
            
            if (programType === 'refer_a_friend') {
                // Referral portal styling
                const inputStyle = state.portalLayoutSettings?.referral_portal_content_input_style || 'white';
                const corners = state.portalLayoutSettings?.referral_portal_content_layout_corners || 'rounded-xl';
                const textColor = state.portalLayoutSettings?.referral_portal_content_text_color || 'gray-800';
                
                const classes = [
                    'transition-shadow block w-full text-sm px-3.5 py-3.5',
                    'border-0 ring-1 ring-inset ring-gray-300 focus:ring-gray-400'
                ];
                
                // Input style (background)
                if (inputStyle === 'white') {
                    classes.push('bg-white text-gray-900');
                } else {
                    if (textColor.startsWith('#')) {
                        classes.push('bg-transparent text-content-custom-text');
                    } else {
                        classes.push(`bg-transparent text-${textColor}`);
                    }
                }
                
                // Corners
                if (corners === 'none') {
                    classes.push('rounded');
                } else {
                    classes.push(corners);
                }
                
                return classes.join(' ');
            } else {
                // Affiliate portal styling (existing logic)
                const defaultStyle = 'transition-shadow block w-full text-sm px-3 py-3 border-0 ring-1 ring-inset ring-gray-300 focus:ring-gray-400';
                if (state.portalLayoutSettings.public_page_content_input_style === 'white') {
                    return [defaultStyle, 'bg-white text-gray-900', state.portalLayoutSettings.public_page_content_layout_corners].join(' ');
                } else {
                    return [defaultStyle, `bg-transparent ${this.getPageContentTextColorClass}`, state.portalLayoutSettings.public_page_content_layout_corners].join(' ');
                }
            }
        },
        getPageContentTextColorClass(state) {
            // Check if it's a referral portal
            const programType = state.program?.program_type?.type || state.program?.programType?.type;
            
            if (programType === 'refer_a_friend') {
                // Referral portal styling
                const textColor = state.portalLayoutSettings?.referral_portal_content_text_color || 'gray-800';
                
                if (textColor.startsWith('#')) {
                    return 'text-content-custom-text';
                } else {
                    return `text-${textColor}`;
                }
            } else {
                // Affiliate portal styling
                return isCustomColor(state.portalLayoutSettings.public_page_content_text_color)
                    ? 'text-custom-content-text'
                    : `text-${state.portalLayoutSettings.public_page_content_text_color}`;
            }
        },
    }
});
